import { ServiceError, ErrorCategory } from './types/serviceInterfaces';

/**
 * Standard error handler for service functions
 * @param error Any caught error
 * @param context Optional context string for better error categorization
 * @returns A standardized ServiceError object
 */
export function handleServiceError(error: unknown, context?: string): ServiceError {
  console.error(`Service error${context ? ` in ${context}` : ''}:`, error);
  
  // Handle Supabase PostgrestError
  if (error && typeof error === 'object' && 'code' in error) {
    const pgError = error as { code: string; message?: string; details?: string };
    
    // Authentication errors
    if (pgError.code === '401' || pgError.code === '403') {
      return {
        category: 'auth',
        message: pgError.message || 'Authentication error',
        details: pgError.details || context
      };
    }
    
    // Not found errors
    if (pgError.code === '404' || pgError.code === '406') {
      return {
        category: 'notFound',
        message: pgError.message || 'Resource not found',
        details: pgError.details || context
      };
    }
    
    // Validation errors
    if (pgError.code.startsWith('22') || pgError.code.startsWith('23')) {
      return {
        category: 'validation',
        message: pgError.message || 'Validation error',
        details: pgError.details || context
      };
    }
    
    // Network errors
    if (pgError.code === 'NETWORK_ERROR' || pgError.code === 'TIMEOUT_ERROR') {
      return {
        category: 'network',
        message: pgError.message || 'Network connection error',
        details: pgError.details || context
      };
    }
    
    // Server errors
    if (pgError.code.startsWith('5')) {
      return {
        category: 'server',
        message: pgError.message || 'Server error',
        details: pgError.details || context
      };
    }
  }
  
  // Standard JS errors
  if (error instanceof Error) {
    // Look for timeout indicators
    if (error.message.includes('timeout') || error.message.includes('timed out')) {
      return {
        category: 'timeout',
        message: error.message,
        details: context
      };
    }
    
    // Look for network indicators
    if (error.message.includes('network') || error.message.includes('connection')) {
      return {
        category: 'network',
        message: error.message,
        details: context
      };
    }
    
    return {
      category: 'unknown',
      message: error.message,
      details: context
    };
  }
  
  // Default unknown error
  return {
    category: 'unknown',
    message: 'An unknown error occurred',
    details: context
  };
}
